import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  Radio,
  TextField,
  Typography,
} from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';
import SwapHorizIcon from '@material-ui/icons/SwapHoriz';
import { useSnackbar } from 'notistack';

import api from '../../services/api';

export interface ISwapSerialItem {
  id: number;
  sku: string | null;
  description: string;
  barcode: string | null;
}

interface IAvailableCode {
  code: string;
  inUse: boolean;
  orderId: number | null;
  orderNumber: string | null;
}

interface ISwapSerialDialogProps {
  open: boolean;
  item: ISwapSerialItem | null;
  orderId: number;
  onClose(): void;
  /** newCode = HEX do serial novo (o que o leitor RFID devolve). */
  onSwapped(itemId: number, newBarcode: string, newCode: string): void;
}

function asciiToHexa(text: string): string {
  let hex = '';
  for (let i = 0; i < text.length; i++) {
    hex += text.charCodeAt(i).toString(16).toUpperCase().padStart(2, '0');
  }
  return hex;
}

// Troca permitida só com o pedido pendente/disponível para separação
// ('new') ou em rota pendente ('on-planned-route') — mesma regra que o
// servidor aplica; usada pelas telas para esconder o botão.
export const canSwapSerial = (statusCode?: string | null): boolean =>
  ['new', 'on-planned-route'].includes(`${statusCode || ''}`);

// Cache curto por SKU: trocar vários itens do mesmo SKU em sequência (o
// caso real) abre o diálogo instantâneo. Invalidado ao confirmar uma troca.
const CACHE_TTL_MS = 30_000;
const codesCache = new Map<string, { at: number; codes: IAvailableCode[] }>();

/// Troca de serial de um item de pedido — compartilhado por Separação,
/// tela de Pedidos e Resumo do Pedido. Lista os seriais do mesmo SKU do
/// catálogo: livres selecionáveis, em uso bloqueados mostrando o pedido.
/// A troca grava barcode (DEC) + code (HEX) no item, auditada no servidor.
export const SwapSerialDialog = ({
  open,
  item,
  orderId,
  onClose,
  onSwapped,
}: ISwapSerialDialogProps) => {
  const { enqueueSnackbar } = useSnackbar();

  const [loading, setLoading] = useState(true);
  const [codes, setCodes] = useState<IAvailableCode[]>([]);
  const [filter, setFilter] = useState('');
  const [selected, setSelected] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    async function load(): Promise<void> {
      if (!open || !item) return;
      setSelected(null);
      setFilter('');

      // cache por SKU dentro do TTL: abre instantâneo
      const cacheKey = item.sku || '';
      const cached = cacheKey ? codesCache.get(cacheKey) : undefined;
      if (cached && Date.now() - cached.at < CACHE_TTL_MS) {
        setCodes(cached.codes);
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const response = await api.get(
          `/operation/separation/items/${item.id}/available-codes`
        );
        if (response.data?.error) {
          enqueueSnackbar(
            `Não foi possível listar os seriais: ${response.data.error}`,
            { variant: 'error' }
          );
          setCodes([]);
          return;
        }
        const list = (response.data?.codes || []) as IAvailableCode[];
        setCodes(list);
        if (cacheKey) codesCache.set(cacheKey, { at: Date.now(), codes: list });
      } catch (error) {
        enqueueSnackbar('Não foi possível listar os seriais.', {
          variant: 'error',
        });
        setCodes([]);
      } finally {
        setLoading(false);
      }
    }

    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, item?.id]);

  const freeCount = useMemo(
    () => codes.filter((code) => !code.inUse).length,
    [codes]
  );

  const visible = useMemo(() => {
    const needle = filter.trim().toUpperCase();
    return codes.filter(
      (code) => !needle || code.code.toUpperCase().includes(needle)
    );
  }, [codes, filter]);

  const handleSwap = useCallback(async () => {
    if (!item || !selected) return;
    setSaving(true);
    try {
      // eventId único para a idempotência do servidor
      const eventId = `portal-${Date.now()}-${Math.random()
        .toString(36)
        .slice(2, 10)}`;
      const response = await api.patch(
        `/operation/separation/items/${item.id}/barcode`,
        {
          eventId,
          newBarcode: selected,
          orderId,
          currentBarcode: item.barcode,
        }
      );
      if (response.data?.error) {
        enqueueSnackbar(
          `Troca recusada: ${response.data.detail || response.data.error}`,
          { variant: 'error' }
        );
        return;
      }
      const applied = (response.data?.newBarcode as string) || selected;
      // o estado "em uso" mudou: a próxima abertura busca fresco
      if (item.sku) codesCache.delete(item.sku);
      enqueueSnackbar(`Serial trocado para ${applied}.`, {
        variant: 'success',
      });
      onSwapped(item.id, applied, asciiToHexa(applied.toUpperCase()));
      onClose();
    } catch (error) {
      enqueueSnackbar('Não foi possível trocar o serial.', {
        variant: 'error',
      });
    } finally {
      setSaving(false);
    }
  }, [enqueueSnackbar, item, onClose, onSwapped, orderId, selected]);

  if (!item) return null;

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle disableTypography>
        <Box display="flex" alignItems="center" gridGap={8}>
          <SwapHorizIcon color="primary" />
          <Typography variant="h6">Trocar serial</Typography>
        </Box>
        <Typography variant="body2" color="textSecondary">
          {item.description} · SKU {item.sku || 'não preenchido'}
        </Typography>
        <Typography variant="body2" color="textSecondary">
          Serial atual do item: <b>{item.barcode || 'não preenchido'}</b>
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          mb={1}
        >
          <Typography variant="body2" color="textSecondary">
            <b>
              {freeCount} livre{freeCount === 1 ? '' : 's'}
            </b>{' '}
            de {codes.length} seria{codes.length === 1 ? 'l' : 'is'} do SKU
          </Typography>
          <TextField
            size="small"
            variant="outlined"
            placeholder="Filtrar serial..."
            value={filter}
            onChange={(event) => setFilter(event.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        <Box
          border={1}
          borderColor="grey.300"
          borderRadius={8}
          maxHeight={300}
          overflow="auto"
        >
          {loading ? (
            <Box p={3} textAlign="center">
              <CircularProgress size={24} />
            </Box>
          ) : visible.length === 0 ? (
            <Box p={2}>
              <Typography variant="body2" color="textSecondary" align="center">
                {codes.length === 0
                  ? 'Nenhum serial cadastrado no catálogo para este SKU.'
                  : 'Nenhum serial bate com o filtro.'}
              </Typography>
            </Box>
          ) : (
            visible.map((entry) => {
              const isCurrent =
                (item.barcode || '').toUpperCase() === entry.code.toUpperCase();
              const disabled = entry.inUse || isCurrent;
              return (
                <Box
                  key={entry.code}
                  display="flex"
                  alignItems="center"
                  gridGap={8}
                  px={1}
                  py={0.25}
                  style={{
                    borderBottom: '1px solid #eef1f2',
                    opacity: disabled ? 0.55 : 1,
                    cursor: disabled ? 'default' : 'pointer',
                    background:
                      selected === entry.code
                        ? 'rgba(19,94,107,0.08)'
                        : undefined,
                  }}
                  onClick={disabled ? undefined : () => setSelected(entry.code)}
                >
                  <Radio
                    size="small"
                    color="primary"
                    checked={selected === entry.code}
                    disabled={disabled}
                  />
                  <Typography
                    variant="body2"
                    style={{ flex: 1, fontFamily: 'monospace', fontWeight: 600 }}
                  >
                    {entry.code}
                  </Typography>
                  {isCurrent ? (
                    <Chip size="small" label="atual deste item" />
                  ) : entry.inUse ? (
                    <Chip
                      size="small"
                      label={
                        entry.orderId === orderId
                          ? 'em uso · neste pedido'
                          : `em uso · ${entry.orderNumber || 'outro pedido'}`
                      }
                    />
                  ) : (
                    <Chip
                      size="small"
                      label="LIVRE"
                      style={{
                        background: '#E7F2EE',
                        color: '#1E7A4C',
                        fontWeight: 700,
                      }}
                    />
                  )}
                </Box>
              );
            })
          )}
        </Box>

        <Typography
          variant="caption"
          color="textSecondary"
          component="div"
          style={{ marginTop: 8 }}
        >
          "Em uso" = presente em outro pedido ativo (ou em outro item deste
          pedido). A troca grava o serial (DEC) e o código RFID (HEX) no item,
          com auditoria.
        </Typography>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSwap}
          disabled={!selected || saving}
          startIcon={
            saving ? <CircularProgress size={16} /> : <SwapHorizIcon />
          }
        >
          {selected ? `Trocar para ${selected}` : 'Trocar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
